import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SnapshotMetadata {
  /** ISO 8601 timestamp of when the snapshot was created. */
  createdAt: string
  /** App version at the time of snapshot (from __APP_VERSION__). */
  appVersion: string
  /** Name of the person who created the snapshot. */
  creatorName: string
  /** Free-form notes about the snapshot. */
  notes: string
}

export interface SnapshotRouteState {
  name: string
  params: Record<string, string>
  query: Record<string, string>
}

export interface SnapshotPageData {
  [key: string]: unknown
}

export interface StateSnapshot {
  metadata: SnapshotMetadata
  stores: Record<string, Record<string, unknown>>
  route: SnapshotRouteState
  pageData: SnapshotPageData
}

// ---------------------------------------------------------------------------
// Store configuration
// ---------------------------------------------------------------------------

/** Minimal interface for Pinia stores used by the snapshot system. */
interface PiniaStoreLike {
  $state: Record<string, any>

  $patch(partialState: Record<string, any>): void
}

export interface SnapshotStoreConfig {
  /** Key used in the snapshot JSON (e.g. 'booking', 'main'). */
  key: string
  /** Pinia store composable function (e.g. useBookingSelectionsStore). */
  useStore: () => PiniaStoreLike
  /** If provided, only these state properties are captured/restored. */
  pick?: string[]
  /** If provided, these state properties are excluded from capture/restore. */
  omit?: string[]
}

export interface SnapshotOptions {
  /** Whether to include view component data in snapshots. Defaults to `false`. */
  includePageData?: boolean
  /** Optional callback invoked before snapshot store data is restored. */
  beforeRestore?: () => void
}

interface ResolvedSnapshotOptions {
  includePageData: boolean
  beforeRestore?: () => void
}

const defaultSnapshotOptions: ResolvedSnapshotOptions = {
  includePageData: false
}

let snapshotStoreConfigs: SnapshotStoreConfig[] = []
let snapshotOptions: ResolvedSnapshotOptions = { ...defaultSnapshotOptions }

/**
 * Registers which Pinia stores are included in snapshots and sets global options.
 * Call once during app initialisation (e.g. in `main.ts`) after Pinia is installed.
 *
 * Each store entry specifies a key, the composable to access it,
 * and optional `pick` or `omit` arrays to filter state properties.
 * When neither `pick` nor `omit` is provided, the entire store state is captured.
 */
export function configureSnapshotStores(
  configs: SnapshotStoreConfig[],
  options?: SnapshotOptions
): void {
  snapshotStoreConfigs = configs
  snapshotOptions = { ...defaultSnapshotOptions, ...options }
}

function pickKeys(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const key of keys) {
    if (key in obj) result[key] = obj[key]
  }
  return result
}

function omitKeys(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = { ...obj }
  for (const key of keys) {
    delete result[key]
  }
  return result
}

function filterState(
  state: Record<string, unknown>,
  config: SnapshotStoreConfig
): Record<string, unknown> {
  if (config.pick) return pickKeys(state, config.pick)
  if (config.omit) return omitKeys(state, config.omit)
  return state
}

// ---------------------------------------------------------------------------
// Pending page data (module-scoped)
// ---------------------------------------------------------------------------

let pendingPageData: SnapshotPageData | null = null

/**
 * Returns and clears the pending page data saved by `restoreSnapshot`.
 * Called by view components in their `mounted()` hook.
 */
export function consumePendingPageData(): SnapshotPageData | null {
  const data = pendingPageData
  pendingPageData = null
  return data
}

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

/**
 * Captures the current application state as a `StateSnapshot`.
 *
 * @param metadata - Creator-supplied metadata fields.
 * @param route - The current Vue Router route object.
 * @param pageData - The current page component's `$data`.
 */
export function captureSnapshot(
  metadata: SnapshotMetadata,
  route: RouteLocationNormalizedLoaded,
  pageData: SnapshotPageData
): StateSnapshot {
  const stores: Record<string, Record<string, unknown>> = {}

  for (const config of snapshotStoreConfigs) {
    const store = config.useStore()
    const raw = JSON.parse(JSON.stringify(store.$state)) as Record<string, unknown>
    stores[config.key] = filterState(raw, config)
  }

  return {
    metadata,
    stores,
    route: {
      name: (route.name as string) ?? '',
      params: { ...route.params } as Record<string, string>,
      query: { ...route.query } as Record<string, string>
    },
    pageData: snapshotOptions.includePageData ? JSON.parse(JSON.stringify(pageData)) : {}
  }
}

// ---------------------------------------------------------------------------
// Compression helpers (Compression Streams API — supported in all modern browsers)
// ---------------------------------------------------------------------------

async function compressBlob(blob: Blob): Promise<Blob> {
  const stream = blob.stream().pipeThrough(new CompressionStream('gzip'))
  return new Response(stream).blob()
}

async function decompressBlob(blob: Blob): Promise<string> {
  const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'))
  return new Response(stream).text()
}

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

/**
 * Serializes a snapshot to gzip-compressed JSON and triggers a file download.
 * Filename format: `cady-snapshot-YYYY-MM-DD-HHmmss.json.gz`
 */
export async function downloadSnapshot(snapshot: StateSnapshot): Promise<void> {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const timestamp = [
    now.getFullYear(),
    '-',
    pad(now.getMonth() + 1),
    '-',
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds())
  ].join('')

  const json = JSON.stringify(snapshot)
  const raw = new Blob([json], { type: 'application/json' })
  const compressed = await compressBlob(raw)
  const url = URL.createObjectURL(compressed)
  const a = document.createElement('a')
  a.href = url
  a.download = `cady-booking-snapshot-${timestamp}.gz`
  a.click()
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Read / Validate
// ---------------------------------------------------------------------------

/**
 * Reads a snapshot file and parses it as a `StateSnapshot`.
 * Accepts both gzip-compressed (`.json.gz`) and plain (`.json`) files.
 * Throws if the file is invalid or missing required keys.
 */
export async function readSnapshotFile(file: File): Promise<StateSnapshot> {
  try {
    const isGzipped = file.name.endsWith('.gz')
    const text = isGzipped ? await decompressBlob(file) : await file.text()
    const parsed = JSON.parse(text)
    validateSnapshot(parsed)
    return parsed as StateSnapshot
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err))
  }
}

function validateSnapshot(obj: unknown): void {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('Snapshot file does not contain a valid JSON object.')
  }

  const snap = obj as Record<string, unknown>

  if (!snap.metadata || typeof snap.metadata !== 'object') {
    throw new Error('Snapshot is missing "metadata".')
  }
  if (!snap.stores || typeof snap.stores !== 'object') {
    throw new Error('Snapshot is missing "stores".')
  }
  if (!snap.route || typeof snap.route !== 'object') {
    throw new Error('Snapshot is missing "route".')
  }

  const stores = snap.stores as Record<string, unknown>
  for (const config of snapshotStoreConfigs) {
    if (!stores[config.key] || typeof stores[config.key] !== 'object') {
      throw new Error(`Snapshot is missing "stores.${config.key}".`)
    }
  }
}

// ---------------------------------------------------------------------------
// Restore
// ---------------------------------------------------------------------------

/**
 * Restores application state from a snapshot:
 * 1. Patches registered Pinia stores with their snapshot data.
 * 2. Saves page data as pending (consumed by the target view's `mounted` hook).
 * 3. Navigates to the snapshot's route.
 */
export async function restoreSnapshot(snapshot: StateSnapshot, router: Router): Promise<void> {
  if (snapshotOptions.beforeRestore) {
    snapshotOptions.beforeRestore()
  }

  for (const config of snapshotStoreConfigs) {
    const storeData = snapshot.stores[config.key]
    if (storeData) {
      const store = config.useStore()
      store.$patch(storeData)
    }
  }

  // Store page data for consumption after navigation
  pendingPageData = snapshotOptions.includePageData ? (snapshot.pageData ?? null) : null

  // Navigate to the captured route
  const { name, params, query } = snapshot.route
  if (name) {
    await router.push({ name, params, query })
  }
}
